import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  COMMUNITY_DEFAULT_DESCRIPTION,
  COMMUNITY_DEFAULT_NAME,
} from "@/lib/concord/config";
import type { Membership } from "@/lib/concord/types";
import type { CommunityListState } from "@/lib/concord/membership";
import { useCommunityActions } from "@/hooks/useCommunityActions";

/**
 * One-time genesis card: shown to the moderator until the chama exists.
 * Founding mints the community secrets, publishes the two genesis editions,
 * and stores the owner's vault in her Community List.
 */
export function FoundCommunity({
  membership,
  communityList,
  onFounded,
}: {
  membership: Membership | undefined;
  communityList: CommunityListState;
  onFounded: () => void;
}) {
  const [name, setName] = useState(COMMUNITY_DEFAULT_NAME);
  const [description, setDescription] = useState(COMMUNITY_DEFAULT_DESCRIPTION);
  const { found } = useCommunityActions({
    membership,
    communityList,
    onChanged: onFounded,
  });

  return (
    <Card className="shadow-md border-primary/20">
      <CardHeader>
        <CardTitle className="font-display text-2xl flex items-center gap-2">
          <Sparkles className="size-5 text-gold" />
          Found the chama
        </CardTitle>
        <CardDescription>
          You are the moderator, and there is no community yet. Founding
          creates the shared keys, publishes the genesis editions
          (<span className="font-mono text-xs">#general</span> included), and
          makes you the owner — the same community Armada will see.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || found.isPending) return;
            found.mutate({ name: name.trim(), description: description.trim() });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="community-name">Community name</Label>
            <Input
              id="community-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={64}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="community-description">Description</Label>
            <Textarea
              id="community-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={10000}
            />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={!name.trim() || found.isPending}>
            {found.isPending && <Loader2 className="size-4 animate-spin" />}
            Found the chama
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            This happens once. The community&rsquo;s identity binds to your
            Nostr key forever.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
